import React from "react";
import { Link } from "react-router-dom";
import { AuthImage } from "@/components/AuthImage";
import { priceLabel, money } from "@/lib/pricing";

// product: Product, sale: optional { sale_price, badge_text }, variant: "grid" | "list"
export function ProductCard({ product, sale, index = 0, variant = "grid" }) {
  const label = priceLabel(product);
  const hasSale =
    sale && sale.sale_price != null && (product.pricing_mode || "single") === "single" && sale.sale_price < product.price;

  const priceBlock = label.quote ? (
    <span className="font-display text-lg" style={{ color: "var(--brand)" }}>Price on request</span>
  ) : (
    <>
      {label.prefix && <span className="text-xs" style={{ color: "var(--muted)" }}>{label.prefix}</span>}
      {hasSale ? (
        <>
          <span className="font-display text-xl" style={{ color: "var(--brand)" }}>{money(sale.sale_price)}</span>
          <span className="text-sm line-through" style={{ color: "var(--muted)" }}>{money(product.price)}</span>
        </>
      ) : (
        <span className="font-display text-xl" style={{ color: "var(--brand)" }}>
          {money(label.value)}
          {label.suffix && <span className="text-sm font-sans"> {label.suffix}</span>}
        </span>
      )}
    </>
  );

  if (variant === "list") {
    return (
      <Link
        to={`/product/${product.id}`}
        data-testid={`product-card-${product.id}`}
        className="group flex gap-4 rounded-2xl overflow-hidden card-lift animate-fade-up p-3"
        style={{ background: "var(--surface)", border: "1px solid var(--border)", animationDelay: `${index * 30}ms` }}
      >
        <div className="relative h-28 w-28 sm:h-32 sm:w-32 shrink-0 rounded-xl overflow-hidden" style={{ background: "var(--surface-2)" }}>
          <AuthImage
            src={product.image_url}
            alt={product.name}
            className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
          />
          {hasSale && (
            <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide shadow" style={{ background: "var(--accent)", color: "#1a1400" }}>
              {sale.badge_text || "Special"}
            </span>
          )}
          {!product.available && (
            <div className="absolute inset-0 bg-black/45 flex items-center justify-center">
              <span className="text-white font-semibold text-[11px] uppercase tracking-widest">Sold out</span>
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0 py-1 flex flex-col">
          <div className="text-[11px] uppercase tracking-widest mb-1" style={{ color: "var(--muted)" }}>{product.category}</div>
          <h3 className="font-display text-lg leading-snug" style={{ color: "var(--ink)" }}>{product.name}</h3>
          <p className="mt-1 text-sm line-clamp-2 flex-1" style={{ color: "var(--muted)" }}>{product.description}</p>
          <div className="mt-2 flex items-baseline gap-2">{priceBlock}</div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/product/${product.id}`}
      data-testid={`product-card-${product.id}`}
      className="group block rounded-2xl overflow-hidden card-lift animate-fade-up"
      style={{ background: "var(--surface)", border: "1px solid var(--border)", animationDelay: `${index * 40}ms` }}
    >
      <div className="relative aspect-[4/3] overflow-hidden" style={{ background: "var(--surface-2)" }}>
        <AuthImage
          src={product.image_url}
          alt={product.name}
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
        />
        {hasSale && (
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide shadow" style={{ background: "var(--accent)", color: "#1a1400" }}>
            {sale.badge_text || "Special"}
          </span>
        )}
        {!product.available && (
          <div className="absolute inset-0 bg-black/45 flex items-center justify-center">
            <span className="text-white font-semibold text-sm uppercase tracking-widest">Sold out</span>
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="text-[11px] uppercase tracking-widest mb-1" style={{ color: "var(--muted)" }}>{product.category}</div>
        <h3 className="font-display text-lg leading-snug" style={{ color: "var(--ink)" }}>{product.name}</h3>
        <p className="mt-1 text-sm line-clamp-2" style={{ color: "var(--muted)" }}>{product.description}</p>
        <div className="mt-3 flex items-baseline gap-2">{priceBlock}</div>
      </div>
    </Link>
  );
}
